import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import PromptTagEditor from '../components/PromptTagEditor';
import PromptTagReferenceLabel from '../components/PromptTagReferenceLabel';
import MainAnalysisConfigurator from './MainAnalysisConfigurator';
import ExtraAnalysisConfigurator from './ExtraAnalysisConfigurator';
import GeneralAnalyticsComponent from './GeneralAnalyticsComponent';
import { EventEmitterType, useEventEmitterContext } from '../context/event-emitter.js';
import {
  QUESTIONS_TAG,
  buildExtraPromptTagReferences,
  buildQuestionTagReference,
  dedupeTagReferences,
  extractPromptTags,
  getMismatchedExtraPromptTagWarning,
  tagReferencesToKnownTags,
} from '../lib/promptTagReferences';

const DEFAULT_SCRIPT_NAME = 'Voxco test 11/12';
const AGENT_API_BASE_URL = process.env.REACT_APP_AGENT_API_URL || '';
const ANALYTICS_API_URL = AGENT_API_BASE_URL ? `${AGENT_API_BASE_URL}/api` : '/api';
const KNOWN_PROMPT_TAGS = [];
const KNOWN_PROMPT_TAG_PATTERNS = [/^<sentiment_[^<>]+>$/i];
const GENERAL_ANALYTICS_MENU_ITEM = 'General Analytics';

const normalizeScriptOptionName = (value) => String(value || '').trim().toLowerCase();

const normalizeExtraAnalysisList = (extraAnalysis, validExtraAnalysisTypeSet) => {
  if (!Array.isArray(extraAnalysis)) {
    return [];
  }

  const safeExtraTypeSet = validExtraAnalysisTypeSet instanceof Set
    ? validExtraAnalysisTypeSet
    : new Set(['none']);

  const seenTypes = new Set();
  const normalized = [];

  extraAnalysis.forEach((value) => {
    const normalizedType = String(value || 'none').trim().toLowerCase();
    const nextType = safeExtraTypeSet.has(normalizedType) ? normalizedType : 'none';
    if (nextType === 'none') {
      normalized.push('none');
      return;
    }
    if (!seenTypes.has(nextType)) {
      seenTypes.add(nextType);
      normalized.push(nextType);
    }
  });

  return normalized;
};

const normalizeSettings = (settings, validAnalysisTypeSet, validExtraAnalysisTypeSet) => {
  const normalizedType = String(settings?.type || 'none').trim().toLowerCase();
  const safeValidTypeSet = validAnalysisTypeSet instanceof Set ? validAnalysisTypeSet : new Set(['none']);
  return {
    type: safeValidTypeSet.has(normalizedType) ? normalizedType : 'none',
    extra_analysis: normalizeExtraAnalysisList(settings?.extra_analysis, validExtraAnalysisTypeSet),
  };
};

const areStringArraysEqual = (left, right) => {
  if (!Array.isArray(left) || !Array.isArray(right)) return false;
  if (left.length !== right.length) return false;
  return left.every((value, index) => value === right[index]);
};

const mergeKnownTags = (...groups) => {
  const seen = new Set();
  const tags = [];

  groups
    .flat()
    .forEach((value) => {
      const tag = String(value || '').trim();
      if (!tag) return;
      const normalizedTag = tag.toLowerCase();
      if (seen.has(normalizedTag)) return;
      seen.add(normalizedTag);
      tags.push(tag);
    });

  return tags;
};

const getSetupLoadEndpoint = (analysisType, configurableAnalysisTypeSet) => {
  const normalizedType = String(analysisType || '').trim().toLowerCase();
  if (normalizedType === 'survey') return '/analysis-opinion-ai-setup';
  if (configurableAnalysisTypeSet instanceof Set && configurableAnalysisTypeSet.has(normalizedType)) {
    return '/analysis-hospital-ai-setup';
  }
  return '';
};

const getSetupSaveEndpoint = (analysisType, configurableAnalysisTypeSet) => {
  const normalizedType = String(analysisType || '').trim().toLowerCase();
  if (normalizedType === 'survey') return '/analysis-opinion-ai-setup/save';
  if (configurableAnalysisTypeSet instanceof Set && configurableAnalysisTypeSet.has(normalizedType)) {
    return '/analysis-hospital-ai-setup/save';
  }
  return '';
};

const AnalyticsSetupPageScriptSetup = ({
  initialSelection,
  onSelectionApplied,
  mainAnalysisTypes,
  extraAnalysisTypes,
  wizardStep = null,
  showConfigureMainAnalysisButton = true,
  showExtraAnalysisConfigureButton = true,
  showGenerateButton = true,
  showPurgeAnalysisLogsButton = true,
  allowedScriptId = null,
}) => {
  const { eventEmitter } = useEventEmitterContext();
  const analysisTypeOptions = useMemo(() => (
    Object.values(mainAnalysisTypes || {})
      .map((item) => ({
        id: String(item?.id || item?.name || '').trim().toLowerCase(),
        label: String(item?.label || '').trim(),
        configurable: !!item?.settings?.configurable,
      }))
      .filter((item) => item.id)
  ), [mainAnalysisTypes]);

  const validAnalysisTypeSet = useMemo(() => (
    new Set(analysisTypeOptions.map((item) => item.id))
  ), [analysisTypeOptions]);

  const defaultAnalysisType = useMemo(() => (
    validAnalysisTypeSet.has('none') ? 'none' : (analysisTypeOptions[0]?.id || 'none')
  ), [analysisTypeOptions, validAnalysisTypeSet]);

  const configurableAnalysisTypeSet = useMemo(() => (
    new Set(analysisTypeOptions.filter((item) => item.configurable).map((item) => item.id))
  ), [analysisTypeOptions]);

  const analysisEditorTitles = useMemo(() => (
    analysisTypeOptions.reduce((accumulator, item) => {
      const sourceLabel = item.label || item.id || 'Analysis';
      accumulator[item.id] = `${sourceLabel} Analysis`;
      return accumulator;
    }, {})
  ), [analysisTypeOptions]);

  const extraAnalysisTypeOptions = useMemo(() => (
    Object.values(extraAnalysisTypes || {})
      .map((item) => ({
        id: String(item?.id || item?.name || '').trim().toLowerCase(),
        label: String(item?.label || '').trim(),
        configurable: !!item?.settings?.configurable,
      }))
      .filter((item) => item.id)
  ), [extraAnalysisTypes]);

  const validExtraAnalysisTypeSet = useMemo(() => (
    new Set(extraAnalysisTypeOptions.map((item) => item.id))
  ), [extraAnalysisTypeOptions]);

  const configurableExtraAnalysisTypeSet = useMemo(() => (
    new Set(extraAnalysisTypeOptions.filter((item) => item.configurable).map((item) => item.id))
  ), [extraAnalysisTypeOptions]);

  const extraAnalysisLabels = useMemo(() => (
    extraAnalysisTypeOptions.reduce((accumulator, item) => {
      accumulator[item.id] = item.label || item.id;
      return accumulator;
    }, {})
  ), [extraAnalysisTypeOptions]);

  const extraAnalysisEditorTitles = useMemo(() => (
    extraAnalysisTypeOptions.reduce((accumulator, item) => {
      const sourceLabel = item.label || item.id || 'Analysis';
      accumulator[item.id] = `${sourceLabel} Analysis`;
      return accumulator;
    }, {})
  ), [extraAnalysisTypeOptions]);

  const [scriptOptions, setScriptOptions] = useState([]);
  const [selectedScriptId, setSelectedScriptId] = useState('');

  const [scriptSettings, setScriptSettings] = useState({ type: defaultAnalysisType, extra_analysis: [] });
  const [settingsDraft, setSettingsDraft] = useState({ type: defaultAnalysisType, extra_analysis: [] });
  const [settingsSaveState, setSettingsSaveState] = useState('idle');

  const [setupData, setSetupData] = useState({
    prompt: '',
    questions: '',
    missingPrompt: false,
    missingQuestions: false,
  });
  const [promptDraft, setPromptDraft] = useState('');
  const [questionsDraft, setQuestionsDraft] = useState('');
  const [setupSaveState, setSetupSaveState] = useState('idle');
  const [generateState, setGenerateState] = useState('idle');

  const [loadingScripts, setLoadingScripts] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [loadingSetup, setLoadingSetup] = useState(false);

  const [settingsError, setSettingsError] = useState('');
  const [setupError, setSetupError] = useState('');
  const [purgeError, setPurgeError] = useState('');
  const [purgeSuccess, setPurgeSuccess] = useState('');
  const [purgeModalOpen, setPurgeModalOpen] = useState(false);
  const [purgeState, setPurgeState] = useState('idle');
  const [purgeCheckState, setPurgeCheckState] = useState('idle');
  const [hasPurgeableLogs, setHasPurgeableLogs] = useState(false);

  const [showConfigEditor, setShowConfigEditor] = useState(false);
  const [isMainConfiguratorCollapsed, setIsMainConfiguratorCollapsed] = useState(false);
  const [selectedExtraAnalysisType, setSelectedExtraAnalysisType] = useState('');
  const [extraSetupCache, setExtraSetupCache] = useState({});
  const [extraPromptDraftByType, setExtraPromptDraftByType] = useState({});
  const [extraSetupSaveStateByType, setExtraSetupSaveStateByType] = useState({});
  const [extraSetupErrorByType, setExtraSetupErrorByType] = useState({});
  const [loadingExtraSetupByType, setLoadingExtraSetupByType] = useState({});
  const [extraConfiguratorCollapsedByType, setExtraConfiguratorCollapsedByType] = useState({});
  const [extraActionConfirmModal, setExtraActionConfirmModal] = useState({
    open: false,
    title: '',
    message: '',
    confirmLabel: 'Yes',
    action: null
  });
  const [templatePickerModal, setTemplatePickerModal] = useState({
    open: false,
    target: '',
    analysisType: '',
    title: ''
  });
  const [templatePickerState, setTemplatePickerState] = useState('idle');
  const [templatePickerError, setTemplatePickerError] = useState('');
  const [templatePickerTemplates, setTemplatePickerTemplates] = useState([]);
  const [selectedTemplatePickerId, setSelectedTemplatePickerId] = useState('');

  const selectedScript = useMemo(
    () => scriptOptions.find((option) => option.id === selectedScriptId),
    [scriptOptions, selectedScriptId]
  );
  const selectedTemplateFromPicker = useMemo(
    () => templatePickerTemplates.find((template) => template.templateId === selectedTemplatePickerId) || null,
    [templatePickerTemplates, selectedTemplatePickerId]
  );

  useEffect(() => {
    setScriptSettings((prev) => normalizeSettings(prev, validAnalysisTypeSet, validExtraAnalysisTypeSet));
    setSettingsDraft((prev) => normalizeSettings(prev, validAnalysisTypeSet, validExtraAnalysisTypeSet));
  }, [validAnalysisTypeSet, validExtraAnalysisTypeSet]);

  const settingsDirty = useMemo(
    () => (
      settingsDraft.type !== scriptSettings.type
      || !areStringArraysEqual(settingsDraft.extra_analysis, scriptSettings.extra_analysis)
    ),
    [settingsDraft.type, scriptSettings.type, settingsDraft.extra_analysis, scriptSettings.extra_analysis]
  );
  const isMainAnalysisTypeDirty = settingsDraft.type !== scriptSettings.type;

  const isSurveyType = settingsDraft.type === 'survey';
  const isConfigurableType = configurableAnalysisTypeSet.has(settingsDraft.type);
  const setupSaveEndpoint = useMemo(
    () => getSetupSaveEndpoint(settingsDraft.type, configurableAnalysisTypeSet),
    [configurableAnalysisTypeSet, settingsDraft.type]
  );
  const analysisEditorTitle = analysisEditorTitles[settingsDraft.type] || 'Analysis';

  const setupDirty = useMemo(() => {
    if (!setupSaveEndpoint) {
      return false;
    }
    if (isSurveyType) {
      return promptDraft !== setupData.prompt || questionsDraft !== setupData.questions;
    }
    if (isConfigurableType) {
      return promptDraft !== setupData.prompt;
    }
    return false;
  }, [isConfigurableType, isSurveyType, promptDraft, questionsDraft, setupData.prompt, setupData.questions, setupSaveEndpoint]);
  const shouldAutoShowMainConfigurator = (
    showConfigureMainAnalysisButton
    && !!selectedScriptId
    && !loadingSettings
    && isConfigurableType
    && !isMainAnalysisTypeDirty
  );
  const mainSetupSaveDisabled = (
    !selectedScriptId
    || loadingSetup
    || setupSaveState === 'saving'
    || !setupDirty
    || !setupSaveEndpoint
  );

  const promptMissingQuestionsTag = useMemo(() => (
    promptDraft.trim().length > 0 && !promptDraft.includes(QUESTIONS_TAG)
  ), [promptDraft]);
  const showGenerateAnalyticsButton = settingsDraft.type !== 'none';
  const generateAnalyticsDisabled = (
    !selectedScriptId
    || loadingScripts
    || loadingSettings
    || isMainAnalysisTypeDirty
  );
  const saveScriptSettingsDisabled = (
    !selectedScriptId
    || loadingSettings
    || settingsSaveState === 'saving'
    || !settingsDirty
  );
  const shouldShowGenerateButton = showGenerateButton && showGenerateAnalyticsButton;
  const isWizardStepOne = wizardStep === 'step-1';
  const isWizardStepTwo = wizardStep === 'step-2';
  const isWizardStepThree = wizardStep === 'step-3';
  const isWizardMode = isWizardStepOne || isWizardStepTwo || isWizardStepThree;
  const showScriptSetupSection = !isWizardMode || isWizardStepOne;
  const showWizardStepTwoSection = isWizardStepTwo;
  const showWizardStepThreeSection = isWizardStepThree;

  const selectedMainAnalysisTypeLabel = useMemo(() => (
    analysisTypeOptions.find((option) => option.id === settingsDraft.type)?.label || settingsDraft.type || 'none'
  ), [analysisTypeOptions, settingsDraft.type]);
  const hasWizardStepTwoContext = !!selectedScriptId && settingsDraft.type !== 'none';
  const shouldShowWizardStepTwoGuidance = showWizardStepTwoSection && !hasWizardStepTwoContext;
  const shouldShowWizardStepThreeStepOneGuidance = showWizardStepThreeSection && !hasWizardStepTwoContext;

  const configuredExtraAnalysisTypes = useMemo(() => {
    const seenTypes = new Set();
    const configuredTypes = [];

    (settingsDraft.extra_analysis || []).forEach((extraType) => {
      if (extraType === 'none') return;
      if (!configurableExtraAnalysisTypeSet.has(extraType)) return;
      if (seenTypes.has(extraType)) return;
      seenTypes.add(extraType);
      configuredTypes.push(extraType);
    });

    return configuredTypes;
  }, [configurableExtraAnalysisTypeSet, settingsDraft.extra_analysis]);

  const savedConfiguredExtraAnalysisTypes = useMemo(() => {
    const seenTypes = new Set();
    const configuredTypes = [];

    (scriptSettings.extra_analysis || []).forEach((extraType) => {
      if (extraType === 'none') return;
      if (!configurableExtraAnalysisTypeSet.has(extraType)) return;
      if (seenTypes.has(extraType)) return;
      seenTypes.add(extraType);
      configuredTypes.push(extraType);
    });

    return configuredTypes;
  }, [configurableExtraAnalysisTypeSet, scriptSettings.extra_analysis]);

  const visibleExtraConfiguratorTypes = useMemo(() => (
    showExtraAnalysisConfigureButton ? savedConfiguredExtraAnalysisTypes : []
  ), [savedConfiguredExtraAnalysisTypes, showExtraAnalysisConfigureButton]);

  const extraPromptTextByType = useMemo(() => {
    const promptByType = {};

    configuredExtraAnalysisTypes.forEach((extraType) => {
      promptByType[extraType] = String(
        extraPromptDraftByType?.[extraType]
        ?? extraSetupCache?.[extraType]?.prompt
        ?? ''
      );
    });

    return promptByType;
  }, [
    configuredExtraAnalysisTypes,
    extraPromptDraftByType,
    extraSetupCache,
  ]);

  const extraTagSourceByTag = useMemo(() => {
    const sourceByTag = new Map();

    configuredExtraAnalysisTypes.forEach((extraType) => {
      const analysisLabel = extraAnalysisLabels[extraType]
        || extraAnalysisEditorTitles[extraType]
        || extraType;

      extractPromptTags(extraPromptTextByType[extraType]).forEach((tag) => {
        const normalizedTag = String(tag || '').toLowerCase();
        if (!normalizedTag || sourceByTag.has(normalizedTag)) return;
        sourceByTag.set(normalizedTag, analysisLabel);
      });
    });

    return sourceByTag;
  }, [
    configuredExtraAnalysisTypes,
    extraAnalysisEditorTitles,
    extraAnalysisLabels,
    extraPromptTextByType
  ]);

  const mainPromptTagReferences = useMemo(() => (
    dedupeTagReferences(
      extractPromptTags(promptDraft).map((tag) => (
        String(tag || '').toLowerCase() === QUESTIONS_TAG.toLowerCase()
          ? buildQuestionTagReference(QUESTIONS_TAG)
          : (
            extraTagSourceByTag.has(String(tag || '').toLowerCase())
              ? {
                tag,
                description: `add ${tag} block from "${extraTagSourceByTag.get(String(tag || '').toLowerCase())}" prompt`
              }
              : { tag, description: 'unknown tag' }
          )
      ))
    )
  ), [extraTagSourceByTag, promptDraft]);

  const getExtraPromptDraftByType = useCallback((extraType) => (
    String(
      extraPromptDraftByType?.[extraType]
      ?? extraSetupCache?.[extraType]?.prompt
      ?? ''
    )
  ), [extraPromptDraftByType, extraSetupCache]);

  const getExtraPromptTagReferencesByType = useCallback((extraType) => (
    dedupeTagReferences(
      extractPromptTags(getExtraPromptDraftByType(extraType)).map((tag) => ({
        tag,
        description: 'block for main prompt'
      }))
    )
  ), [getExtraPromptDraftByType]);

  const getExtraPromptTagWarningByType = useCallback((extraType) => (
    getMismatchedExtraPromptTagWarning(getExtraPromptDraftByType(extraType))
  ), [getExtraPromptDraftByType]);

  const wizardStepThreePromptLoading = useMemo(() => (
    loadingSetup
    || savedConfiguredExtraAnalysisTypes.some((extraType) => !!loadingExtraSetupByType?.[extraType])
  ), [loadingExtraSetupByType, loadingSetup, savedConfiguredExtraAnalysisTypes]);

  const wizardStepThreeHasMissingPromptData = useMemo(() => {
    if (!hasWizardStepTwoContext) {
      return false;
    }

    const needsMainPrompt = isConfigurableType;
    const mainPromptMissing = needsMainPrompt && String(promptDraft || setupData.prompt || '').trim().length === 0;
    const missingExtraPrompt = savedConfiguredExtraAnalysisTypes.some(
      (extraType) => String(getExtraPromptDraftByType(extraType)).trim().length === 0
    );

    return mainPromptMissing || missingExtraPrompt;
  }, [
    getExtraPromptDraftByType,
    hasWizardStepTwoContext,
    isConfigurableType,
    promptDraft,
    savedConfiguredExtraAnalysisTypes,
    setupData.prompt
  ]);

  const shouldShowWizardStepThreeStepTwoGuidance = (
    showWizardStepThreeSection
    && hasWizardStepTwoContext
    && !wizardStepThreePromptLoading
    && wizardStepThreeHasMissingPromptData
  );
  const shouldRenderWizardStepThreeAnalytics = (
    showWizardStepThreeSection
    && hasWizardStepTwoContext
    && !wizardStepThreePromptLoading
    && !shouldShowWizardStepThreeStepTwoGuidance
  );

  const templatePickerPromptTagReferences = useMemo(() => {
    if (templatePickerModal.target === 'main') {
      return dedupeTagReferences(
        extractPromptTags(selectedTemplateFromPicker?.prompt || '').map((tag) => (
          String(tag || '').toLowerCase() === QUESTIONS_TAG.toLowerCase()
            ? buildQuestionTagReference(QUESTIONS_TAG)
            : (
              extraTagSourceByTag.has(String(tag || '').toLowerCase())
                ? {
                  tag,
                  description: `add ${tag} block from "${extraTagSourceByTag.get(String(tag || '').toLowerCase())}" prompt`
                }
                : { tag, description: 'unknown tag' }
            )
        ))
      );
    }

    if (templatePickerModal.target === 'extra') {
      return dedupeTagReferences(
        buildExtraPromptTagReferences({
          promptText: selectedTemplateFromPicker?.prompt || '',
          analysisLabel: extraAnalysisLabels[templatePickerModal.analysisType]
            || extraAnalysisEditorTitles[templatePickerModal.analysisType]
            || templatePickerModal.analysisType
        })
      );
    }

    return [];
  }, [
    extraTagSourceByTag,
    extraAnalysisEditorTitles,
    extraAnalysisLabels,
    selectedTemplateFromPicker?.prompt,
    templatePickerModal.analysisType,
    templatePickerModal.target
  ]);

  const templatePickerExtraTagWarning = useMemo(() => (
    templatePickerModal.target === 'extra'
      ? getMismatchedExtraPromptTagWarning(selectedTemplateFromPicker?.prompt || '')
      : ''
  ), [selectedTemplateFromPicker?.prompt, templatePickerModal.target]);

  const mainPromptKnownTags = useMemo(() => (
    mergeKnownTags(KNOWN_PROMPT_TAGS, tagReferencesToKnownTags(mainPromptTagReferences))
  ), [mainPromptTagReferences]);

  const getExtraPromptKnownTagsByType = useCallback((extraType) => (
    mergeKnownTags(KNOWN_PROMPT_TAGS, tagReferencesToKnownTags(getExtraPromptTagReferencesByType(extraType)))
  ), [getExtraPromptTagReferencesByType]);

  const templatePickerKnownTags = useMemo(() => (
    mergeKnownTags(KNOWN_PROMPT_TAGS, tagReferencesToKnownTags(templatePickerPromptTagReferences))
  ), [templatePickerPromptTagReferences]);

  const resetSetupDraft = useCallback(() => {
    setSetupData({
      prompt: '',
      questions: '',
      missingPrompt: false,
      missingQuestions: false,
    });
    setPromptDraft('');
    setQuestionsDraft('');
    setSetupSaveState('idle');
    setGenerateState('idle');
    setSetupError('');
  }, []);

  const resetAllExtraConfiguratorState = useCallback(() => {
    setSelectedExtraAnalysisType('');
    setExtraPromptDraftByType({});
    setExtraSetupSaveStateByType({});
    setExtraSetupErrorByType({});
    setLoadingExtraSetupByType({});
    setExtraConfiguratorCollapsedByType({});
  }, []);

  const fetchExtraSetupData = useCallback(async (scriptId, extraAnalysisType) => {
    const { data } = await api.post('/analysis-extra-ai-setup', {
      scriptId,
      extraAnalysisType,
    }, { baseURL: ANALYTICS_API_URL });

    if (!data?.success) {
      throw new Error(data?.message || 'Failed to load extra analysis setup data');
    }

    return {
      prompt: String(data?.prompt || ''),
      missingPrompt: !!data?.missingPrompt,
    };
  }, []);

  const fetchPromptTemplatesByAnalysisType = useCallback(async (analysisType) => {
    const { data } = await api.get('/analysis-prompt-templates', {
      params: { analysisType },
      baseURL: ANALYTICS_API_URL,
    });

    if (!data?.success) {
      throw new Error(data?.message || 'Failed to load prompt templates');
    }

    return Array.isArray(data?.templates)
      ? data.templates.map((item, index) => ({
        templateId: String(item?.templateId || `template-${index}`),
        name: String(item?.name || '').trim(),
        prompt: String(item?.prompt || ''),
      }))
      : [];
  }, []);

  const loadScriptSettings = useCallback(async (scriptId) => {
    if (!scriptId) {
      const defaults = { type: defaultAnalysisType, extra_analysis: [] };
      setScriptSettings(defaults);
      setSettingsDraft(defaults);
      setSettingsSaveState('idle');
      setShowConfigEditor(false);
      setIsMainConfiguratorCollapsed(false);
      resetSetupDraft();
      resetAllExtraConfiguratorState();
      setExtraSetupCache({});
      setExtraActionConfirmModal({
        open: false,
        title: '',
        message: '',
        confirmLabel: 'Yes',
        action: null
      });
      setTemplatePickerModal({
        open: false,
        target: '',
        analysisType: '',
        title: ''
      });
      setTemplatePickerState('idle');
      setTemplatePickerError('');
      setTemplatePickerTemplates([]);
      setSelectedTemplatePickerId('');
      return;
    }

    setLoadingSettings(true);
    setSettingsError('');
    setSettingsSaveState('idle');
    setShowConfigEditor(false);
    setIsMainConfiguratorCollapsed(false);
    resetSetupDraft();
    resetAllExtraConfiguratorState();
    setExtraSetupCache({});
    setExtraActionConfirmModal({
      open: false,
      title: '',
      message: '',
      confirmLabel: 'Yes',
      action: null
    });
    setTemplatePickerModal({
      open: false,
      target: '',
      analysisType: '',
      title: ''
    });
    setTemplatePickerState('idle');
    setTemplatePickerError('');
    setTemplatePickerTemplates([]);
    setSelectedTemplatePickerId('');

    try {
      const { data } = await api.get(`/analysis-scripts/get/${scriptId}`, { baseURL: ANALYTICS_API_URL });
      if (!data?.success) {
        throw new Error(data?.message || 'Failed to load script settings');
      }

      const nextSettings = normalizeSettings(
        data?.script?.settings,
        validAnalysisTypeSet,
        validExtraAnalysisTypeSet
      );
      setScriptSettings(nextSettings);
      setSettingsDraft(nextSettings);
    } catch (error) {
      const defaults = { type: defaultAnalysisType, extra_analysis: [] };
      setScriptSettings(defaults);
      setSettingsDraft(defaults);
      setSettingsError(error?.message || 'Failed to load script settings');
    } finally {
      setLoadingSettings(false);
    }
  }, [
    defaultAnalysisType,
    resetAllExtraConfiguratorState,
    resetSetupDraft,
    validAnalysisTypeSet,
    validExtraAnalysisTypeSet
  ]);

  const loadSetupData = useCallback(async (scriptId, analysisType = settingsDraft.type) => {
    const endpoint = getSetupLoadEndpoint(analysisType, configurableAnalysisTypeSet);
    if (!scriptId || !endpoint) {
      resetSetupDraft();
      return;
    }

    setLoadingSetup(true);
    setSetupError('');

    try {
      const { data } = await api.post(endpoint, { scriptId }, { baseURL: ANALYTICS_API_URL });
      if (!data?.success) {
        throw new Error(data?.message || 'Failed to load setup data');
      }

      const promptText = data?.prompt || '';
      const questionsText = analysisType === 'survey' ? (data?.questions || '') : '';
      setSetupData({
        prompt: promptText,
        questions: questionsText,
        missingPrompt: !!data?.missingPrompt,
        missingQuestions: analysisType === 'survey' ? !!data?.missingQuestions : false,
      });
      setPromptDraft(promptText);
      setQuestionsDraft(questionsText);
      setSetupSaveState('idle');
      setGenerateState('idle');
    } catch (error) {
      resetSetupDraft();
      setSetupError(error?.message || 'Failed to load setup data');
    } finally {
      setLoadingSetup(false);
    }
  }, [configurableAnalysisTypeSet, resetSetupDraft, settingsDraft.type]);

  const loadExtraSetupData = useCallback(async (scriptId, extraAnalysisType) => {
    if (!scriptId || !configurableExtraAnalysisTypeSet.has(extraAnalysisType)) {
      return;
    }

    setLoadingExtraSetupByType((prev) => ({ ...prev, [extraAnalysisType]: true }));
    setExtraSetupErrorByType((prev) => ({ ...prev, [extraAnalysisType]: '' }));
    setSelectedExtraAnalysisType(extraAnalysisType);

    try {
      const setup = await fetchExtraSetupData(scriptId, extraAnalysisType);
      setExtraPromptDraftByType((prev) => ({ ...prev, [extraAnalysisType]: setup.prompt }));
      setExtraSetupSaveStateByType((prev) => ({ ...prev, [extraAnalysisType]: 'idle' }));
      setExtraSetupCache((prev) => ({ ...prev, [extraAnalysisType]: setup }));
    } catch (error) {
      setExtraSetupErrorByType((prev) => ({
        ...prev,
        [extraAnalysisType]: error?.message || 'Failed to load extra analysis setup data'
      }));
    } finally {
      setLoadingExtraSetupByType((prev) => ({ ...prev, [extraAnalysisType]: false }));
    }
  }, [configurableExtraAnalysisTypeSet, fetchExtraSetupData]);

  const closeExtraActionConfirmModal = useCallback(() => {
    setExtraActionConfirmModal({
      open: false,
      title: '',
      message: '',
      confirmLabel: 'Yes',
      action: null
    });
  }, []);

  const openExtraActionConfirmModal = useCallback(({
    title,
    message,
    confirmLabel = 'Yes',
    action
  }) => {
    setExtraActionConfirmModal({
      open: true,
      title,
      message,
      confirmLabel,
      action
    });
  }, []);

  const closeTemplatePickerModal = useCallback(() => {
    if (templatePickerState === 'loading') return;
    setTemplatePickerModal({
      open: false,
      target: '',
      analysisType: '',
      title: ''
    });
    setTemplatePickerState('idle');
    setTemplatePickerError('');
    setTemplatePickerTemplates([]);
    setSelectedTemplatePickerId('');
  }, [templatePickerState]);

  const openTemplatePickerModal = useCallback(async ({
    target,
    analysisType,
    title
  }) => {
    if (!analysisType) return;

    setTemplatePickerModal({
      open: true,
      target,
      analysisType,
      title: title || 'Pick a Template'
    });
    setTemplatePickerState('loading');
    setTemplatePickerError('');
    setTemplatePickerTemplates([]);
    setSelectedTemplatePickerId('');

    try {
      const templates = await fetchPromptTemplatesByAnalysisType(analysisType);
      setTemplatePickerTemplates(templates);
      setSelectedTemplatePickerId(templates[0]?.templateId || '');
      setTemplatePickerState('idle');
    } catch (error) {
      setTemplatePickerState('idle');
      setTemplatePickerError(error?.message || 'Failed to load templates');
    }
  }, [fetchPromptTemplatesByAnalysisType]);

  const toggleMainConfiguratorCollapsed = useCallback(() => {
    setIsMainConfiguratorCollapsed((prev) => !prev);
  }, []);

  const discardMainSetupChanges = useCallback(() => {
    setPromptDraft(setupData.prompt);
    setQuestionsDraft(setupData.questions);
    setSetupError('');
    setSetupSaveState('idle');
    setGenerateState('idle');
  }, [setupData.prompt, setupData.questions]);

  const requestDiscardMainSetupChanges = useCallback(() => {
    if (!setupDirty) {
      discardMainSetupChanges();
      return;
    }

    openExtraActionConfirmModal({
      title: 'Discard Changes',
      message: 'Are you sure you want to discard changes?',
      confirmLabel: 'Yes',
      action: discardMainSetupChanges
    });
  }, [discardMainSetupChanges, openExtraActionConfirmModal, setupDirty]);

  const toggleExtraConfiguratorCollapsed = useCallback((extraAnalysisType) => {
    setExtraConfiguratorCollapsedByType((prev) => ({
      ...prev,
      [extraAnalysisType]: !prev?.[extraAnalysisType]
    }));
  }, []);

  const discardExtraSetupChanges = useCallback((extraAnalysisType) => {
    const savedPrompt = String(extraSetupCache?.[extraAnalysisType]?.prompt || '');
    setExtraPromptDraftByType((prev) => ({ ...prev, [extraAnalysisType]: savedPrompt }));
    setExtraSetupSaveStateByType((prev) => ({ ...prev, [extraAnalysisType]: 'idle' }));
    setExtraSetupErrorByType((prev) => ({ ...prev, [extraAnalysisType]: '' }));
  }, [extraSetupCache]);

  const requestDiscardExtraSetupChanges = useCallback((extraAnalysisType, hasUnsavedChanges) => {
    if (!hasUnsavedChanges) {
      discardExtraSetupChanges(extraAnalysisType);
      return;
    }

    openExtraActionConfirmModal({
      title: 'Discard Changes',
      message: 'Are you sure you want to discard changes?',
      confirmLabel: 'Yes',
      action: () => discardExtraSetupChanges(extraAnalysisType)
    });
  }, [discardExtraSetupChanges, openExtraActionConfirmModal]);

  const applyPickedTemplatePrompt = useCallback(() => {
    if (!selectedTemplateFromPicker) return;

    if (templatePickerModal.target === 'main') {
      setPromptDraft(selectedTemplateFromPicker.prompt);
      setSetupSaveState('idle');
      setSetupError('');
    } else if (templatePickerModal.target === 'extra') {
      const targetExtraType = String(templatePickerModal.analysisType || '').trim().toLowerCase();
      if (!targetExtraType) {
        closeTemplatePickerModal();
        return;
      }

      setExtraPromptDraftByType((prev) => ({ ...prev, [targetExtraType]: selectedTemplateFromPicker.prompt }));
      setExtraSetupSaveStateByType((prev) => ({ ...prev, [targetExtraType]: 'idle' }));
      setExtraSetupErrorByType((prev) => ({ ...prev, [targetExtraType]: '' }));
    }

    closeTemplatePickerModal();
  }, [
    closeTemplatePickerModal,
    selectedTemplateFromPicker,
    templatePickerModal.analysisType,
    templatePickerModal.target
  ]);

  const handleSelectTemplateFromPicker = useCallback(() => {
    if (!selectedTemplateFromPicker) return;

    const targetExtraType = String(templatePickerModal.analysisType || '').trim().toLowerCase();
    const currentPrompt = templatePickerModal.target === 'main'
      ? promptDraft
      : getExtraPromptDraftByType(targetExtraType);

    const hasExistingPrompt = currentPrompt.trim().length > 0
      && currentPrompt !== selectedTemplateFromPicker.prompt;

    if (hasExistingPrompt) {
      openExtraActionConfirmModal({
        title: 'Overwrite Prompt',
        message: 'Are you sure you want to overwrite existing prompt with a template?',
        confirmLabel: 'Yes',
        action: applyPickedTemplatePrompt
      });
      return;
    }

    applyPickedTemplatePrompt();
  }, [
    applyPickedTemplatePrompt,
    getExtraPromptDraftByType,
    openExtraActionConfirmModal,
    promptDraft,
    selectedTemplateFromPicker,
    templatePickerModal.analysisType,
    templatePickerModal.target
  ]);

  const applyRemoveExtraAnalysisAtIndex = useCallback((index) => {
    let removedType = 'none';
    setSettingsDraft((prev) => {
      const nextExtraAnalysis = [...(prev.extra_analysis || [])];
      if (index < 0 || index >= nextExtraAnalysis.length) {
        return prev;
      }
      removedType = nextExtraAnalysis[index] || 'none';
      nextExtraAnalysis.splice(index, 1);
      return normalizeSettings({
        ...prev,
        extra_analysis: nextExtraAnalysis
      }, validAnalysisTypeSet, validExtraAnalysisTypeSet);
    });

    if (removedType !== 'none') {
      setExtraSetupCache((prev) => {
        const next = { ...prev };
        delete next[removedType];
        return next;
      });
      setExtraPromptDraftByType((prev) => {
        const next = { ...prev };
        delete next[removedType];
        return next;
      });
      setExtraSetupSaveStateByType((prev) => {
        const next = { ...prev };
        delete next[removedType];
        return next;
      });
      setExtraSetupErrorByType((prev) => {
        const next = { ...prev };
        delete next[removedType];
        return next;
      });
      setLoadingExtraSetupByType((prev) => {
        const next = { ...prev };
        delete next[removedType];
        return next;
      });
      setExtraConfiguratorCollapsedByType((prev) => {
        const next = { ...prev };
        delete next[removedType];
        return next;
      });
      if (selectedExtraAnalysisType === removedType) {
        setSelectedExtraAnalysisType('');
      }
    }
  }, [selectedExtraAnalysisType, validAnalysisTypeSet, validExtraAnalysisTypeSet]);

  const applyUpdateExtraAnalysisTypeAtIndex = useCallback((index, nextType) => {
    let previousType = 'none';
    setSettingsDraft((prev) => {
      const nextExtraAnalysis = [...(prev.extra_analysis || [])];
      if (index < 0 || index >= nextExtraAnalysis.length) {
        return prev;
      }
      previousType = nextExtraAnalysis[index] || 'none';
      nextExtraAnalysis[index] = nextType;
      return normalizeSettings({
        ...prev,
        extra_analysis: nextExtraAnalysis
      }, validAnalysisTypeSet, validExtraAnalysisTypeSet);
    });

    if (previousType !== nextType && previousType !== 'none') {
      setExtraSetupCache((prev) => {
        const next = { ...prev };
        delete next[previousType];
        return next;
      });
      setExtraPromptDraftByType((prev) => {
        const next = { ...prev };
        delete next[previousType];
        return next;
      });
      setExtraSetupSaveStateByType((prev) => {
        const next = { ...prev };
        delete next[previousType];
        return next;
      });
      setExtraSetupErrorByType((prev) => {
        const next = { ...prev };
        delete next[previousType];
        return next;
      });
      setLoadingExtraSetupByType((prev) => {
        const next = { ...prev };
        delete next[previousType];
        return next;
      });
      setExtraConfiguratorCollapsedByType((prev) => {
        const next = { ...prev };
        delete next[previousType];
        return next;
      });
      if (selectedExtraAnalysisType === previousType) {
        setSelectedExtraAnalysisType('');
      }
    }
  }, [selectedExtraAnalysisType, validAnalysisTypeSet, validExtraAnalysisTypeSet]);

  const resolveHasPromptForExtraType = useCallback(async (extraAnalysisType) => {
    if (!selectedScriptId || !configurableExtraAnalysisTypeSet.has(extraAnalysisType)) {
      return false;
    }

    const draftPrompt = String(extraPromptDraftByType?.[extraAnalysisType] || '').trim();
    if (draftPrompt.length > 0) {
      return true;
    }

    const cachedSetup = extraSetupCache[extraAnalysisType];
    if (cachedSetup && typeof cachedSetup.prompt === 'string') {
      return cachedSetup.prompt.trim().length > 0;
    }

    const setup = await fetchExtraSetupData(selectedScriptId, extraAnalysisType);
    setExtraSetupCache((prev) => ({ ...prev, [extraAnalysisType]: setup }));
    return setup.prompt.trim().length > 0;
  }, [
    selectedScriptId,
    extraPromptDraftByType,
    extraSetupCache,
    configurableExtraAnalysisTypeSet,
    fetchExtraSetupData
  ]);

  const handleAddExtraAnalysis = () => {
    if (!selectedScriptId || loadingSettings) return;
    setSettingsError('');
    setSettingsDraft((prev) => normalizeSettings({
      ...prev,
      extra_analysis: [...(prev.extra_analysis || []), 'none']
    }, validAnalysisTypeSet, validExtraAnalysisTypeSet));
  };

  const handleRemoveExtraAnalysisAtIndex = (index) => {
    if (!selectedScriptId || loadingSettings) return;

    openExtraActionConfirmModal({
      title: 'Remove Extra Analysis',
      message: 'Are you sure you want to remove this extra analysis? Its saved extra-analysis prompt will be removed after you save script settings.',
      confirmLabel: 'Yes, remove',
      action: () => applyRemoveExtraAnalysisAtIndex(index)
    });
  };

  const handleExtraAnalysisTypeChange = async (index, nextTypeRaw) => {
    if (!selectedScriptId || loadingSettings) return;

    const normalizedNextType = validExtraAnalysisTypeSet.has(nextTypeRaw) ? nextTypeRaw : 'none';
    const currentExtraAnalysis = settingsDraft.extra_analysis || [];
    const currentType = currentExtraAnalysis[index] || 'none';

    if (normalizedNextType === currentType) {
      return;
    }

    const duplicateTypeSelected = (
      normalizedNextType !== 'none'
      && currentExtraAnalysis.some((type, typeIndex) => typeIndex !== index && type === normalizedNextType)
    );

    if (duplicateTypeSelected) {
      setSettingsError(`"${extraAnalysisLabels[normalizedNextType] || normalizedNextType}" can be selected only once.`);
      return;
    }

    setSettingsError('');

    try {
      const isTypeChanging = currentType !== 'none' && currentType !== normalizedNextType;
      if (isTypeChanging) {
        const hasPrompt = await resolveHasPromptForExtraType(currentType);
        if (hasPrompt) {
          openExtraActionConfirmModal({
            title: 'Change Extra Analysis Type',
            message: 'Changing this extra analysis type will remove its saved prompt after you save script settings. Continue?',
            confirmLabel: 'Yes, change type',
            action: () => applyUpdateExtraAnalysisTypeAtIndex(index, normalizedNextType)
          });
          return;
        }
      }

      applyUpdateExtraAnalysisTypeAtIndex(index, normalizedNextType);
    } catch (error) {
      setSettingsError(error?.message || 'Failed to validate extra analysis prompt');
    }
  };

  useEffect(() => {
    let isMounted = true;

    const loadScripts = async () => {
      setLoadingScripts(true);
      setSettingsError('');

      try {
        const { data } = await api.get('/scripts', { baseURL: ANALYTICS_API_URL });
        if (!data?.success) {
          throw new Error(data?.message || 'Failed to load scripts');
        }

        const scripts = Array.isArray(data?.scripts) ? data.scripts : [];
        const options = scripts
          .map((script) => ({
            id: String(script?._id || ''),
            name: String(script?.scriptName || 'Untitled script').trim() || 'Untitled script'
          }))
          .filter((option) => option.id);

        if (!isMounted) return;

        setScriptOptions(options);

        setSelectedScriptId((currentId) => {
          if (currentId && options.some((option) => option.id === currentId)) {
            return currentId;
          }

          const defaultScript = options.find((option) => option.name === DEFAULT_SCRIPT_NAME);
          return defaultScript?.id || options[0]?.id || '';
        });
      } catch (error) {
        if (isMounted) {
          setScriptOptions([]);
          setSelectedScriptId('');
          setSettingsError(error?.message || 'Failed to load scripts');
        }
      } finally {
        if (isMounted) {
          setLoadingScripts(false);
        }
      }
    };

    loadScripts();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!initialSelection) return;
    if (!scriptOptions.length) return;

    const incomingId = String(initialSelection?.scriptId || '').trim();
    const incomingName = String(initialSelection?.scriptName || '').trim();

    let targetOption = null;
    if (incomingId) {
      targetOption = scriptOptions.find((option) => option.id === incomingId) || null;
    }

    if (!targetOption && incomingName) {
      const normalizedIncomingName = normalizeScriptOptionName(incomingName);
      targetOption = scriptOptions.find(
        (option) => normalizeScriptOptionName(option.name) === normalizedIncomingName
      ) || null;
    }

    if (targetOption?.id) {
      setSelectedScriptId(targetOption.id);
    }

    onSelectionApplied?.();
  }, [initialSelection, scriptOptions, onSelectionApplied]);

  useEffect(() => {
    loadScriptSettings(selectedScriptId);
  }, [selectedScriptId, loadScriptSettings]);

  useEffect(() => {
    if (!selectedScriptId) return undefined;
    if (loadingSettings) return undefined;
    if (visibleExtraConfiguratorTypes.length === 0) return undefined;

    const missingTypes = visibleExtraConfiguratorTypes.filter((extraType) => !extraSetupCache?.[extraType]);

    if (missingTypes.length === 0) return undefined;

    let canceled = false;

    const hydrateExtraPrompts = async () => {
      const setupEntries = await Promise.all(
        missingTypes.map(async (extraType) => {
          try {
            const setup = await fetchExtraSetupData(selectedScriptId, extraType);
            return [extraType, setup];
          } catch (error) {
            return null;
          }
        })
      );

      if (canceled) return;

      const nextCacheEntries = {};
      setupEntries.forEach((entry) => {
        if (!entry) return;
        const [extraType, setup] = entry;
        nextCacheEntries[extraType] = setup;
      });

      if (Object.keys(nextCacheEntries).length === 0) return;

      setExtraSetupCache((prev) => ({
        ...prev,
        ...nextCacheEntries
      }));
    };

    hydrateExtraPrompts();

    return () => {
      canceled = true;
    };
  }, [
    visibleExtraConfiguratorTypes,
    extraSetupCache,
    fetchExtraSetupData,
    loadingSettings,
    selectedScriptId,
  ]);

  useEffect(() => {
    const validTypes = new Set(visibleExtraConfiguratorTypes);
    const pruneMapByValidTypes = (map = {}) => (
      Object.fromEntries(Object.entries(map).filter(([type]) => validTypes.has(type)))
    );

    setExtraPromptDraftByType((prev) => pruneMapByValidTypes(prev));
    setExtraSetupSaveStateByType((prev) => pruneMapByValidTypes(prev));
    setExtraSetupErrorByType((prev) => pruneMapByValidTypes(prev));
    setLoadingExtraSetupByType((prev) => pruneMapByValidTypes(prev));
    setExtraConfiguratorCollapsedByType((prev) => pruneMapByValidTypes(prev));

    if (!validTypes.has(selectedExtraAnalysisType)) {
      setSelectedExtraAnalysisType('');
    }
  }, [visibleExtraConfiguratorTypes, selectedExtraAnalysisType]);

  useEffect(() => {
    if (!shouldAutoShowMainConfigurator) {
      setShowConfigEditor(false);
      setIsMainConfiguratorCollapsed(false);
      return;
    }

    if (showConfigEditor) {
      return;
    }

    let canceled = false;
    const openAndLoadMainConfigurator = async () => {
      setShowConfigEditor(true);
      setIsMainConfiguratorCollapsed(false);
      await loadSetupData(selectedScriptId, settingsDraft.type);
      if (canceled) return;
    };

    openAndLoadMainConfigurator();

    return () => {
      canceled = true;
    };
  }, [
    loadSetupData,
    selectedScriptId,
    settingsDraft.type,
    shouldAutoShowMainConfigurator,
    showConfigEditor
  ]);

  const handleSaveScriptSettings = async () => {
    if (!selectedScriptId) return;

    setSettingsSaveState('saving');
    setSettingsError('');

    try {
      const { data } = await api.post('/analysis-scripts/save', {
        scriptId: selectedScriptId,
        settings: settingsDraft,
      }, { baseURL: ANALYTICS_API_URL });

      if (!data?.success) {
        throw new Error(data?.message || 'Failed to save script settings');
      }

      const nextSettings = normalizeSettings(
        data?.script?.settings || settingsDraft,
        validAnalysisTypeSet,
        validExtraAnalysisTypeSet
      );
      setScriptSettings(nextSettings);
      setSettingsDraft(nextSettings);

      if (!configurableAnalysisTypeSet.has(nextSettings.type)) {
        setShowConfigEditor(false);
        setIsMainConfiguratorCollapsed(false);
      }

      setSettingsSaveState('saved');
      window.setTimeout(() => setSettingsSaveState('idle'), 2000);
    } catch (error) {
      setSettingsSaveState('idle');
      setSettingsError(error?.message || 'Failed to save script settings');
    }
  };

  const handleOpenExtraConfigurator = async (extraAnalysisType) => {
    if (!selectedScriptId || !configurableExtraAnalysisTypeSet.has(extraAnalysisType)) return;

    setExtraConfiguratorCollapsedByType((prev) => ({ ...prev, [extraAnalysisType]: false }));
    await loadExtraSetupData(selectedScriptId, extraAnalysisType);
  };

  const handleSaveSetup = async () => {
    if (!selectedScriptId || !isConfigurableType) return;

    if (!setupSaveEndpoint) {
      setSetupError('Saving is not supported for this analysis type.');
      return;
    }

    setSetupSaveState('saving');
    setSetupError('');

    try {
      const payload = {
        scriptId: selectedScriptId,
        scriptName: selectedScript?.name,
        prompt: promptDraft,
        ...(isSurveyType ? { questions: questionsDraft } : {}),
      };
      const { data } = await api.post(setupSaveEndpoint, payload, { baseURL: ANALYTICS_API_URL });

      if (!data?.success) {
        throw new Error(data?.message || 'Failed to save setup data');
      }

      setSetupData((prev) => ({
        ...prev,
        prompt: promptDraft,
        questions: isSurveyType ? questionsDraft : '',
        missingPrompt: false,
        missingQuestions: isSurveyType ? false : prev.missingQuestions,
      }));

      setSetupSaveState('saved');
      window.setTimeout(() => setSetupSaveState('idle'), 2000);
    } catch (error) {
      setSetupSaveState('idle');
      setSetupError(error?.message || 'Failed to save setup data');
    }
  };

  const handleSaveExtraSetup = async (extraAnalysisType) => {
    if (!selectedScriptId || !configurableExtraAnalysisTypeSet.has(extraAnalysisType)) return;

    const promptValue = String(extraPromptDraftByType?.[extraAnalysisType] ?? extraSetupCache?.[extraAnalysisType]?.prompt ?? '');
    setExtraSetupSaveStateByType((prev) => ({ ...prev, [extraAnalysisType]: 'saving' }));
    setExtraSetupErrorByType((prev) => ({ ...prev, [extraAnalysisType]: '' }));

    try {
      const { data } = await api.post('/analysis-extra-ai-setup/save', {
        scriptId: selectedScriptId,
        scriptName: selectedScript?.name,
        extraAnalysisType,
        prompt: promptValue,
      }, { baseURL: ANALYTICS_API_URL });

      if (!data?.success) {
        throw new Error(data?.message || 'Failed to save extra analysis setup data');
      }

      const nextSetup = {
        prompt: promptValue,
        missingPrompt: !promptValue.trim(),
      };
      setExtraSetupCache((prev) => ({ ...prev, [extraAnalysisType]: nextSetup }));
      setExtraSetupSaveStateByType((prev) => ({ ...prev, [extraAnalysisType]: 'saved' }));
      window.setTimeout(() => {
        setExtraSetupSaveStateByType((prev) => ({ ...prev, [extraAnalysisType]: 'idle' }));
      }, 2000);
    } catch (error) {
      setExtraSetupSaveStateByType((prev) => ({ ...prev, [extraAnalysisType]: 'idle' }));
      setExtraSetupErrorByType((prev) => ({
        ...prev,
        [extraAnalysisType]: error?.message || 'Failed to save extra analysis setup data'
      }));
    }
  };

  const handleGenerate = async (type) => {
    if (!selectedScriptId || !isSurveyType) return;

    setGenerateState('generating');
    setSetupError('');

    try {
      const { data } = await api.post('/analysis-opinion-ai-setup/generate', {
        scriptId: selectedScriptId,
        scriptName: selectedScript?.name,
        type,
      }, { baseURL: ANALYTICS_API_URL });

      if (!data?.success) {
        throw new Error(data?.message || 'Failed to generate setup data');
      }

      await loadSetupData(selectedScriptId, settingsDraft.type);
      setGenerateState('generated');
      window.setTimeout(() => setGenerateState('idle'), 2000);
    } catch (error) {
      setGenerateState('idle');
      setSetupError(error?.message || 'Failed to generate setup data');
    }
  };

  const checkPurgeAvailability = useCallback(async (scriptId) => {
    if (!scriptId) {
      setHasPurgeableLogs(false);
      setPurgeCheckState('idle');
      return false;
    }

    setPurgeCheckState('checking');
    setPurgeError('');

    try {
      const { data } = await api.post('/analysis-logs/has-data', { scriptId }, { baseURL: ANALYTICS_API_URL });
      if (!data?.success) {
        throw new Error(data?.message || 'Failed to check analysis logs');
      }

      const hasLogs = !!data?.hasLogs;
      setHasPurgeableLogs(hasLogs);
      return hasLogs;
    } catch (error) {
      setHasPurgeableLogs(false);
      setPurgeError(error?.message || 'Failed to check analysis logs');
      return false;
    } finally {
      setPurgeCheckState('idle');
    }
  }, []);

  useEffect(() => {
    if (!selectedScriptId) {
      setHasPurgeableLogs(false);
      setPurgeCheckState('idle');
      setPurgeModalOpen(false);
      setPurgeError('');
      return;
    }

    checkPurgeAvailability(selectedScriptId);
  }, [selectedScriptId, settingsDraft.type, checkPurgeAvailability]);

  const openPurgeModal = () => {
    if (purgeState === 'purging' || purgeCheckState === 'checking' || !hasPurgeableLogs) {
      return;
    }
    setPurgeError('');
    setPurgeModalOpen(true);
  };

  const closePurgeModal = () => {
    if (purgeState === 'purging') return;
    setPurgeModalOpen(false);
    setPurgeError('');
  };

  const handlePurgeAnalysisLogs = async () => {
    if (!selectedScriptId) return;

    setPurgeState('purging');
    setPurgeError('');
    setPurgeSuccess('');

    try {
      const { data } = await api.post('/analysis-logs/purge', {
        scriptId: selectedScriptId,
      }, { baseURL: ANALYTICS_API_URL });

      if (!data?.success) {
        throw new Error(data?.message || 'Failed to purge analysis logs');
      }

      setPurgeModalOpen(false);
      setPurgeState('idle');
      setPurgeSuccess(`Purged ${data.deletedCount || 0} analysis logs for "${selectedScript?.name || 'selected script'}".`);
      await checkPurgeAvailability(selectedScriptId);
      window.setTimeout(() => setPurgeSuccess(''), 3000);
    } catch (error) {
      setPurgeState('idle');
      setPurgeError(error?.message || 'Failed to purge analysis logs');
    }
  };

  const handleOpenGenerateAnalytics = () => {
    if (!selectedScriptId || settingsDraft.type === 'none') return;

    eventEmitter?.emit({
      type: EventEmitterType.DashboardSelectMenu,
      payload: {
        menuItem: GENERAL_ANALYTICS_MENU_ITEM,
        params: {
          scriptId: selectedScriptId,
          scriptName: selectedScript?.name || '',
          highlightGenerate: true,
        },
      },
    });
  };

  const shouldRenderMainConfigurator = (
    showConfigEditor
    && isConfigurableType
    && showConfigureMainAnalysisButton
    && (!isWizardMode || showWizardStepTwoSection)
    && (!showWizardStepTwoSection || hasWizardStepTwoContext)
  );

  const shouldRenderExtraConfigurators = (
    (!isWizardMode || showWizardStepTwoSection)
    && (!showWizardStepTwoSection || hasWizardStepTwoContext)
  );

  return (
    <>
      {showScriptSetupSection && (
        <>
          {isWizardStepOne && (
            <div className="space-y-1">
              <h2 className="text-[1.125rem] font-semibold text-gray-900">
                Choose script &amp; analysis type
              </h2>
              <p className="text-[0.9375rem] text-gray-500">
                Select a script and assign its analysis type and extra analyses. Save to apply.
              </p>
            </div>
          )}

          <section className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 space-y-4">
        <div className="grid gap-3 md:grid-cols-[auto_minmax(240px,1fr)_auto_minmax(160px,220px)] md:items-center">
          <label className="text-sm font-semibold text-gray-700" htmlFor="analytics-script-select">
            Script
          </label>
          <select
            id="analytics-script-select"
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={selectedScriptId}
            onChange={(event) => setSelectedScriptId(event.target.value)}
            disabled={loadingScripts || scriptOptions.length === 0}
          >
            {scriptOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>

          <label className="text-sm font-semibold text-gray-700" htmlFor="analytics-type-select">
            Type
          </label>
          <select
            id="analytics-type-select"
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={settingsDraft.type}
            onChange={(event) => setSettingsDraft((prev) => normalizeSettings({
              ...prev,
              type: event.target.value
            }, validAnalysisTypeSet, validExtraAnalysisTypeSet))}
            disabled={!selectedScriptId || loadingSettings}
          >
            {analysisTypeOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label || option.id}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50/40 p-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">Extra Analysis</h3>
            <button
              type="button"
              onClick={handleAddExtraAnalysis}
              disabled={!selectedScriptId || loadingSettings}
              className={`inline-flex w-24 items-center justify-center rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
                !selectedScriptId || loadingSettings
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-emerald-600 text-white hover:bg-emerald-700'
              }`}
              title="Add extra analysis"
            >
              + Add
            </button>
          </div>

          {settingsDraft.extra_analysis.length === 0 && (
            <div className="text-xs text-gray-500">
              No extra analyses configured.
            </div>
          )}

          {settingsDraft.extra_analysis.length > 0 && (
            <div className="grid grid-cols-[1fr_auto] gap-2 items-start">
              <div className="space-y-2">
                {settingsDraft.extra_analysis.map((extraType, index) => (
                  <div key={`extra-analysis-${index}`} className="grid gap-2 md:grid-cols-[minmax(200px,260px)_auto] md:items-center">
                    <select
                      className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={extraType}
                      onChange={(event) => handleExtraAnalysisTypeChange(index, event.target.value)}
                      disabled={!selectedScriptId || loadingSettings}
                    >
                      {extraAnalysisTypeOptions.map((option) => {
                        const optionType = option.id;
                        const isDuplicate = (
                          optionType !== 'none'
                          && optionType !== extraType
                          && settingsDraft.extra_analysis.some((currentType, currentIndex) => (
                            currentIndex !== index && currentType === optionType
                          ))
                        );
                        return (
                          <option
                            key={`${index}-${optionType}`}
                            value={optionType}
                            disabled={isDuplicate}
                          >
                            {extraAnalysisLabels[optionType] || optionType}
                          </option>
                        );
                      })}
                    </select>

                    <span className="hidden md:block" />
                  </div>
                ))}
              </div>

              <div className="flex flex-col gap-2">
                {settingsDraft.extra_analysis.map((_, index) => (
                  <button
                    key={`extra-analysis-remove-${index}`}
                    type="button"
                    onClick={() => handleRemoveExtraAnalysisAtIndex(index)}
                    disabled={!selectedScriptId || loadingSettings}
                    className={`inline-flex w-24 items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold transition ${
                      !selectedScriptId || loadingSettings
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-rose-600 text-white hover:bg-rose-700'
                    }`}
                    title="Remove extra analysis"
                  >
                    - Remove
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleSaveScriptSettings}
              disabled={saveScriptSettingsDisabled}
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition ${
                saveScriptSettingsDisabled
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {settingsSaveState === 'saving' ? 'Saving…' : 'Save Script Settings'}
            </button>

            {(loadingScripts || loadingSettings) && (
              <span className="text-xs text-gray-400">
                {loadingScripts ? 'Loading scripts…' : 'Loading settings…'}
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {shouldShowGenerateButton && (
              <button
                type="button"
                onClick={handleOpenGenerateAnalytics}
                disabled={generateAnalyticsDisabled}
                className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition ${
                  generateAnalyticsDisabled
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-sky-600 text-white hover:bg-sky-700'
                }`}
              >
                Generate
              </button>
            )}

            {showPurgeAnalysisLogsButton && (
              <button
                type="button"
                onClick={openPurgeModal}
                disabled={
                  !selectedScriptId
                  || loadingScripts
                  || loadingSettings
                  || purgeState === 'purging'
                  || purgeCheckState === 'checking'
                  || !hasPurgeableLogs
                }
                className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition ${
                  !selectedScriptId
                  || loadingScripts
                  || loadingSettings
                  || purgeState === 'purging'
                  || purgeCheckState === 'checking'
                  || !hasPurgeableLogs
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-rose-600 text-white hover:bg-rose-700'
                }`}
              >
                {purgeState === 'purging'
                  ? 'Purging…'
                  : purgeCheckState === 'checking'
                    ? 'Checking logs…'
                    : 'Purge analysis logs'}
              </button>
            )}
          </div>
        </div>

        {settingsError && (
          <div className="text-sm text-red-600">{settingsError}</div>
        )}

        {settingsSaveState === 'saved' && (
          <div className="text-sm text-emerald-600">Script settings saved successfully.</div>
        )}

        {purgeSuccess && (
          <div className="text-sm text-emerald-600">{purgeSuccess}</div>
        )}

        {purgeError && !purgeModalOpen && (
          <div className="text-sm text-red-600">{purgeError}</div>
        )}
          </section>
        </>
      )}

      {showWizardStepTwoSection && (
        <section className="space-y-4">
          <div className="space-y-1">
            <h2 className="text-[1.125rem] font-semibold text-gray-900">Prompt &amp; questions</h2>
            <p className="text-[0.9375rem] text-gray-500">
              Customize the analysis prompt and, for survey scripts, the questions. Use &lt;questions&gt; in the prompt to inject survey questions.
            </p>
          </div>

          {!!selectedScriptId && (
            <div className="grid gap-3 rounded-lg border border-gray-200 bg-gray-50/50 p-3 sm:grid-cols-2">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Script</div>
                <div className="text-sm font-semibold text-gray-900">
                  {selectedScript?.name || 'Selected script'}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Main analysis type</div>
                <div className="text-sm font-semibold text-gray-900">
                  {selectedMainAnalysisTypeLabel || 'None'}
                </div>
              </div>
            </div>
          )}

          {shouldShowWizardStepTwoGuidance && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
              Go to Step 1 to choose a script and analysis type.
            </div>
          )}
        </section>
      )}

      {showWizardStepThreeSection && (
        <section className="space-y-4">
          <div className="space-y-1">
            <h2 className="text-[1.125rem] font-semibold text-gray-900">Run and results</h2>
            <p className="text-[0.9375rem] text-gray-500">
              Run analysis on the script you chose and configured. Results appear below..
            </p>
          </div>

          {shouldShowWizardStepThreeStepOneGuidance && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
              Go to Step 1 to choose a script and analysis type.
            </div>
          )}

          {shouldShowWizardStepThreeStepTwoGuidance && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
              Go to Step 2 to configure prompts
            </div>
          )}

          {showWizardStepThreeSection && hasWizardStepTwoContext && wizardStepThreePromptLoading && (
            <div className="text-sm text-gray-500">
              Loading prompt configuration...
            </div>
          )}

          {shouldRenderWizardStepThreeAnalytics && (
            <GeneralAnalyticsComponent
              embeddedInWizard
              hideConfigureButton
              fixedScriptSelection={{
                scriptId: selectedScriptId,
                scriptName: selectedScript?.name || '',
                scriptType: settingsDraft.type
              }}
              allowedScriptId={allowedScriptId}
            />
          )}
        </section>
      )}

      {shouldRenderMainConfigurator && (
        <MainAnalysisConfigurator
          title={analysisEditorTitle}
          onSave={handleSaveSetup}
          onDiscard={requestDiscardMainSetupChanges}
          highlightTemplatePicker={showWizardStepTwoSection && promptDraft.trim().length === 0}
          isCollapsed={isMainConfiguratorCollapsed}
          onToggleCollapsed={toggleMainConfiguratorCollapsed}
          saveDisabled={mainSetupSaveDisabled}
          discardDisabled={!setupDirty}
          setupSaveState={setupSaveState}
          setupData={setupData}
          isSurveyType={isSurveyType}
          loadingSetup={loadingSetup}
          setupError={setupError}
          generateState={generateState}
          promptMissingQuestionsTag={promptMissingQuestionsTag}
          questionsTag={QUESTIONS_TAG}
          onOpenTemplatePicker={() => openTemplatePickerModal({
            target: 'main',
            analysisType: settingsDraft.type,
            title: 'Pick a Template'
          })}
          templatePickerLoading={templatePickerState === 'loading'}
          hasSelectedScript={!!selectedScriptId}
          mainPromptTagReferences={mainPromptTagReferences}
          promptDraft={promptDraft}
          setPromptDraft={setPromptDraft}
          mainPromptKnownTags={mainPromptKnownTags}
          knownPromptTagPatterns={KNOWN_PROMPT_TAG_PATTERNS}
          questionsDraft={questionsDraft}
          setQuestionsDraft={setQuestionsDraft}
          onGenerateQuestions={() => handleGenerate('questions')}
        />
      )}

      {shouldRenderExtraConfigurators && visibleExtraConfiguratorTypes.map((extraAnalysisType) => {
        const extraAnalysisEditorTitle = extraAnalysisEditorTitles[extraAnalysisType] || 'Extra Analysis';
        const extraSetupData = extraSetupCache?.[extraAnalysisType] || { prompt: '', missingPrompt: false };
        const extraPromptDraft = getExtraPromptDraftByType(extraAnalysisType);
        const extraSetupSaveState = extraSetupSaveStateByType?.[extraAnalysisType] || 'idle';
        const extraSetupError = extraSetupErrorByType?.[extraAnalysisType] || '';
        const loadingExtraSetup = !!loadingExtraSetupByType?.[extraAnalysisType];
        const extraSetupDirty = extraPromptDraft !== String(extraSetupData.prompt || '');
        const extraSetupSaveDisabled = (
          !selectedScriptId
          || loadingExtraSetup
          || extraSetupSaveState === 'saving'
          || !extraSetupDirty
        );
        const selectedExtraPromptTagReferences = getExtraPromptTagReferencesByType(extraAnalysisType);
        const selectedExtraPromptTagWarning = getExtraPromptTagWarningByType(extraAnalysisType);
        const selectedExtraPromptKnownTags = getExtraPromptKnownTagsByType(extraAnalysisType);
        const isCollapsed = !!extraConfiguratorCollapsedByType?.[extraAnalysisType];

        return (
          <ExtraAnalysisConfigurator
            key={`extra-configurator-${extraAnalysisType}`}
            title={extraAnalysisEditorTitle}
            onSave={() => handleSaveExtraSetup(extraAnalysisType)}
            onDiscard={() => requestDiscardExtraSetupChanges(extraAnalysisType, extraSetupDirty)}
            highlightTemplatePicker={showWizardStepTwoSection && extraPromptDraft.trim().length === 0}
            isCollapsed={isCollapsed}
            onToggleCollapsed={() => {
              if (isCollapsed) {
                handleOpenExtraConfigurator(extraAnalysisType);
                return;
              }
              toggleExtraConfiguratorCollapsed(extraAnalysisType);
            }}
            saveDisabled={extraSetupSaveDisabled}
            discardDisabled={!extraSetupDirty}
            extraSetupSaveState={extraSetupSaveState}
            extraSetupData={extraSetupData}
            loadingExtraSetup={loadingExtraSetup}
            extraSetupError={extraSetupError}
            selectedExtraAnalysisType={extraAnalysisType}
            onOpenTemplatePicker={() => {
              setSelectedExtraAnalysisType(extraAnalysisType);
              openTemplatePickerModal({
                target: 'extra',
                analysisType: extraAnalysisType,
                title: 'Pick a Template'
              });
            }}
            templatePickerLoading={templatePickerState === 'loading'}
            hasSelectedScript={!!selectedScriptId}
            selectedExtraPromptTagReferences={selectedExtraPromptTagReferences}
            selectedExtraPromptTagWarning={selectedExtraPromptTagWarning}
            extraPromptDraft={extraPromptDraft}
            setExtraPromptDraft={(value) => {
              setExtraPromptDraftByType((prev) => ({ ...prev, [extraAnalysisType]: value }));
            }}
            selectedExtraPromptKnownTags={selectedExtraPromptKnownTags}
            knownPromptTagPatterns={KNOWN_PROMPT_TAG_PATTERNS}
          />
        );
      })}

      {templatePickerModal.open && (
        <div className="fixed inset-0 z-[81] flex items-center justify-center bg-slate-900/55 px-4">
          <div className="w-full max-w-4xl rounded-xl border border-slate-200 bg-white shadow-2xl">
            <div className="border-b border-slate-200 px-5 py-4">
              <h3 className="text-base font-semibold text-slate-900">
                {templatePickerModal.title || 'Pick a Template'}
              </h3>
            </div>

            <div className="grid gap-4 px-5 py-4 lg:grid-cols-[minmax(220px,280px)_1fr]">
              <div className="space-y-2">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Templates
                </div>

                {templatePickerState === 'loading' && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
                    Loading templates…
                  </div>
                )}

                {templatePickerError && (
                  <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                    {templatePickerError}
                  </div>
                )}

                {templatePickerState !== 'loading' && !templatePickerError && templatePickerTemplates.length === 0 && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
                    No templates available for this analysis type.
                  </div>
                )}

                {templatePickerTemplates.length > 0 && (
                  <div className="max-h-[340px] space-y-2 overflow-y-auto pr-1">
                    {templatePickerTemplates.map((template, index) => {
                      const isActive = template.templateId === selectedTemplatePickerId;
                      return (
                        <button
                          key={template.templateId}
                          type="button"
                          onClick={() => setSelectedTemplatePickerId(template.templateId)}
                          className={`w-full rounded-lg border px-3 py-2 text-left transition ${
                            isActive
                              ? 'border-blue-300 bg-blue-50'
                              : 'border-slate-200 bg-white hover:bg-slate-50'
                          }`}
                        >
                          <div className="truncate text-sm font-semibold text-slate-800">
                            {template.name || `Template ${index + 1}`}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Preview
                </div>
                <PromptTagReferenceLabel items={templatePickerPromptTagReferences} />
                {templatePickerExtraTagWarning && (
                  <div className="text-xs text-amber-700">
                    {templatePickerExtraTagWarning}
                  </div>
                )}
                <PromptTagEditor
                  value={selectedTemplateFromPicker?.prompt || ''}
                  readOnly
                  tone="slate"
                  knownTags={templatePickerKnownTags}
                  knownTagPatterns={KNOWN_PROMPT_TAG_PATTERNS}
                  minHeightClassName="min-h-[380px]"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-4">
              <button
                type="button"
                onClick={closeTemplatePickerModal}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSelectTemplateFromPicker}
                disabled={!selectedTemplateFromPicker || templatePickerState === 'loading'}
                className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                  !selectedTemplateFromPicker || templatePickerState === 'loading'
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-emerald-600 text-white hover:bg-emerald-700'
                }`}
              >
                Select template
              </button>
            </div>
          </div>
        </div>
      )}

      {extraActionConfirmModal.open && (
        <div className="fixed inset-0 z-[82] flex items-center justify-center bg-slate-900/55 px-4">
          <div className="w-full max-w-lg rounded-xl border border-amber-200 bg-white shadow-2xl">
            <div className="border-b border-amber-100 px-5 py-4">
              <h3 className="text-base font-semibold text-slate-900">
                {extraActionConfirmModal.title || 'Confirm Action'}
              </h3>
            </div>
            <div className="space-y-3 px-5 py-4">
              <p className="text-sm text-slate-700">
                {extraActionConfirmModal.message}
              </p>
              {selectedScript?.name && (
                <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-600">
                  Script: {selectedScript.name}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-4">
              <button
                type="button"
                onClick={closeExtraActionConfirmModal}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const action = extraActionConfirmModal.action;
                  closeExtraActionConfirmModal();
                  action?.();
                }}
                className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-rose-700"
              >
                {extraActionConfirmModal.confirmLabel || 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {purgeModalOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/55 px-4">
          <div className="w-full max-w-lg rounded-xl border border-red-200 bg-white shadow-2xl">
            <div className="border-b border-red-100 px-5 py-4">
              <h3 className="text-base font-semibold text-slate-900">Purge Analysis Logs</h3>
            </div>
            <div className="space-y-3 px-5 py-4">
              <p className="text-sm text-slate-700">
                Are you sure you want to purge all analysis logs for this script? It'll require generating them anew!
              </p>
              {selectedScript?.name && (
                <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-600">
                  Script: {selectedScript.name}
                </div>
              )}
              {purgeError && (
                <div className="text-sm text-red-600">{purgeError}</div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-5 py-4">
              <button
                type="button"
                onClick={closePurgeModal}
                disabled={purgeState === 'purging'}
                className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                  purgeState === 'purging'
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePurgeAnalysisLogs}
                disabled={purgeState === 'purging'}
                className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                  purgeState === 'purging'
                    ? 'bg-rose-300 text-white cursor-not-allowed'
                    : 'bg-rose-600 text-white hover:bg-rose-700'
                }`}
              >
                {purgeState === 'purging' ? 'Purging…' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AnalyticsSetupPageScriptSetup;
